/**
 * Node catalog — the single source of truth for every workflow node type's
 * label, icon and colour.
 *
 * Before this file there were **five** divergent node-colour maps and **four**
 * divergent label records:
 *
 *   - `AIWorkflowWizard`'s `nodeIconMap`      — the only spec-correct one (5 functional tones)
 *   - `NodeConfigPanel`'s `nodeTypeInfo`      — 21 distinct hues, alpha scale
 *   - `WorkflowSidebar`'s `nodeCategories`    — 21 distinct hues, `-100 / -900/40` scale
 *   - all 22 `nodes/*.tsx` cards              — 21 distinct hues, inline literals
 *   - `WorkflowBuilderPage`'s MiniMap switch  — 21 raw hex literals
 *
 * `internalAlert` alone rendered as "Internal Alert" in the config panel and
 * "Team Alert" in five other places.
 *
 * The wizard's map is promoted here because it is the one that follows §3/§26:
 * **colour carries function, the phosphor icon carries identity.** 21 hues told
 * the reader nothing — the icon already distinguished the node, so the hue was
 * pure decoration, and it was the single largest source of off-palette Tailwind
 * utilities in the codebase (teal / cyan / violet / slate / zinc / orange /
 * green / rose, none of which are in the palette).
 *
 * Two deliberate departures from the promoted map:
 *   1. `TONE_NEUTRAL` was `bg-slate-400 dark:bg-slate-600` — slate is a warm-ish
 *      off-palette family (§12 is achromatic). It became `#8a8f98`, the
 *      project's own neutral — and then, on AB#22127, orange: the tone is now
 *      called `internal`, because grey chrome read as "this node is disabled".
 *      See TONE_STYLES.internal.
 *   2. Every tone now carries all six surface forms the five call sites need
 *      (solid / handle / border / borderIdle / chip / text) plus a raw hex for
 *      the MiniMap, which cannot take a class.
 *
 * **Tailwind JIT:** every class string here is a complete literal. Never compose
 * a class from a hue fragment plus a shade at runtime — the JIT scanner reads
 * source text, not values, and a composed class silently renders unstyled.
 */

import React from 'react'
import {
  Lightning, Envelope, Clock, GitBranch, Tag, Flask, Target,
  Presentation, MagnifyingGlass, Bell, SignOut, ClipboardText, Globe,
  Gauge, TreeView, ChatText, UserPlus, Brain, ArrowsClockwise, VideoCamera, ArrowBendUpLeft,
} from '@phosphor-icons/react'
import { LinkedInBrandIcon } from '@/components/icons/LinkedInBrandIcon'
import type { NodeType } from '@/types/workflow'

/** Anything renderable as `<Icon className="…" />`. */
export type NodeIcon = React.ComponentType<{ className?: string }>

/**
 * Five functional tones. Colour answers "what kind of thing is this?", never
 * "which node is this?" — the icon answers that.
 */
export type NodeTone =
  | 'brand'    // comms · logic · data — the dominant tone
  | 'success'  // lifecycle: start / goal / meeting booked
  | 'time'     // waits, throttles, anything clock-shaped
  | 'danger'   // exit / terminal
  | 'internal' // internal ops the recipient never sees

export interface ToneStyles {
  /** Solid fill — wizard step dot, timeline marker. */
  solid: string
  /** ReactFlow `<Handle>` fill. Needs the `!` prefix to beat ReactFlow's own CSS. */
  handle: string
  /** Node card border when the node is selected on the canvas. */
  border: string
  /** Node card border at rest. */
  borderIdle: string
  /** Soft icon chip — background + border, dual-mode. */
  chip: string
  /** Icon and eyebrow text, dual-mode. */
  text: string
  /** Raw hex for surfaces that cannot take a class (ReactFlow MiniMap). */
  hex: string
}

export const TONE_STYLES: Record<NodeTone, ToneStyles> = {
  brand: {
    solid: 'bg-[#5e6ad2]',
    handle: '!bg-[#5e6ad2]',
    border: 'border-[#5e6ad2]',
    borderIdle: 'border-[#5e6ad2]/60',
    chip: 'bg-[#5e6ad2]/10 border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30',
    text: 'text-[#5e6ad2] dark:text-[#a5b4fc]',
    hex: '#5e6ad2',
  },
  success: {
    solid: 'bg-emerald-500',
    handle: '!bg-emerald-500',
    border: 'border-emerald-500',
    borderIdle: 'border-emerald-500/60',
    chip: 'bg-emerald-500/10 border-emerald-500/25 dark:bg-emerald-500/[0.14] dark:border-emerald-500/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    hex: '#10b981',
  },
  time: {
    solid: 'bg-amber-500',
    handle: '!bg-amber-500',
    border: 'border-amber-500',
    borderIdle: 'border-amber-500/60',
    chip: 'bg-amber-500/10 border-amber-500/25 dark:bg-amber-500/[0.14] dark:border-amber-500/30',
    text: 'text-amber-600 dark:text-amber-400',
    hex: '#f59e0b',
  },
  danger: {
    solid: 'bg-red-500',
    handle: '!bg-red-500',
    border: 'border-red-500',
    borderIdle: 'border-red-500/60',
    chip: 'bg-red-500/10 border-red-500/25 dark:bg-red-500/[0.14] dark:border-red-500/30',
    text: 'text-red-600 dark:text-red-400',
    hex: '#ef4444',
  },
  // Orange, not grey (AB#22127). Team Alert, Create Task and Assign Owner
  // were the only nodes on this tone, and grey chrome plus
  // `text-muted-foreground` is the universal "disabled" signal — testers read
  // all three as deactivated on the canvas. They are ordinary, usable nodes.
  //
  // Repainting the tone rather than moving those three onto an existing one
  // keeps the rule at the top of this file intact: colour answers "what kind
  // of thing is this?", one tone per meaning. Putting them on `danger` would
  // say "terminal" alongside Exit Workflow; `time` would say "clock-shaped"
  // alongside Delay and Rate Limit. So the fifth tone stays a fifth tone and
  // just stops looking switched off — and because every surface (border, chip,
  // icon, handle, wizard dot, MiniMap) is derived from here, they all move
  // together.
  //
  // Written as a raw hex, not `orange-600`, and that is not a dodge: this
  // file's own palette guard (see nodeCatalog.test.ts, OFF_PALETTE) bans the
  // Tailwind `orange-*` family along with a dozen others, and the two tones
  // here that are project colours rather than Tailwind ones -- `brand`
  // (#5e6ad2) and the grey this replaced (#8a8f98) -- have always been raw
  // hex for exactly that reason. #ea580c is orange-600's value, chosen over
  // orange-500 so it stays tellable apart from `time`'s amber-500 (#f59e0b)
  // and `danger`'s red-500 (#ef4444): deeper than the amber, without reading
  // as red. The dark-mode text lightens the same way brand's does.
  internal: {
    solid: 'bg-[#ea580c]',
    handle: '!bg-[#ea580c]',
    border: 'border-[#ea580c]',
    borderIdle: 'border-[#ea580c]/60',
    chip: 'bg-[#ea580c]/10 border-[#ea580c]/25 dark:bg-[#ea580c]/[0.14] dark:border-[#ea580c]/30',
    text: 'text-[#ea580c] dark:text-[#fb923c]',
    hex: '#ea580c',
  },
}

export interface NodeMeta {
  /** The one user-facing name for this concept. Used by the palette, the config
   *  panel header, the builder's default node data and the playground. */
  label: string
  /** One-line palette description. */
  description: string
  icon: NodeIcon
  tone: NodeTone
}

/**
 * All 22 `NodeType`s. Typed as `Record<NodeType, …>` on purpose — the previous
 * maps were all `Record<string, …>`, so a missing or misspelled key was not a
 * compile error. Adding a node type to the union now fails the build until it
 * is described here.
 */
export const NODE_CATALOG: Record<NodeType, NodeMeta> = {
  trigger:         { label: 'Trigger',          description: 'Start your workflow',                 icon: Lightning,         tone: 'success' },
  sendEmail:       { label: 'Send Email',       description: 'Manual or AI-generated',              icon: Envelope,          tone: 'brand'   },
  delay:           { label: 'Delay',            description: 'Wait for a period',                   icon: Clock,             tone: 'time'    },
  condition:       { label: 'Condition',        description: 'Branch on criteria',                  icon: GitBranch,         tone: 'brand'   },
  action:          { label: 'Action',           description: 'Perform an action',                   icon: Tag,               tone: 'brand'   },
  abTest:          { label: 'A/B Test',         description: 'Split test variants',                 icon: Flask,             tone: 'brand'   },
  goal:            { label: 'Goal',             description: 'Define success criteria',             icon: Target,            tone: 'success' },
  generateDeck:    { label: 'Collaterals',      description: 'AI-generate or attach uploaded files', icon: Presentation,     tone: 'brand'   },
  enrichContacts:  { label: 'Enrich Contacts',  description: 'AI-powered contact research',         icon: MagnifyingGlass,   tone: 'brand'   },
  internalAlert:   { label: 'Team Alert',       description: 'Email or Slack notification',         icon: Bell,              tone: 'internal' },
  exitWorkflow:    { label: 'Exit Workflow',    description: 'Remove from campaign',                icon: SignOut,           tone: 'danger'  },
  createTask:      { label: 'Create Task',      description: 'Sales follow-up task',                icon: ClipboardText,     tone: 'internal' },
  httpRequest:     { label: 'HTTP Request',     description: 'Call external APIs',                  icon: Globe,             tone: 'brand'   },
  rateLimit:       { label: 'Rate Limit',       description: 'Throttle sending rate',               icon: Gauge,             tone: 'time'    },
  branchByField:   { label: 'Branch by Field',  description: 'Multi-way routing',                   icon: TreeView,          tone: 'brand'   },
  linkedInAction:  { label: 'LinkedIn Action',  description: 'LinkedIn outreach task',              icon: LinkedInBrandIcon, tone: 'brand'   },
  smsMessage:      { label: 'Send SMS',         description: 'SMS via Twilio',                      icon: ChatText,          tone: 'brand'   },
  // Blue, not the internal orange (AB#22127 follow-up, stakeholder call).
  // `brand` IS this palette's blue (#5e6ad2) -- the Tailwind blue/sky/indigo
  // families are banned by the guard in nodeCatalog.test.ts, and inventing a
  // second blue would both fight that guard and read as a muddy near-miss of
  // this one on the canvas. Round-robin assignment is a data operation, which
  // is within brand's remit (comms / logic / data).
  assignOwner:     { label: 'Assign Owner',     description: 'Round-robin assignment',              icon: UserPlus,          tone: 'brand'    },
  aiDecision:      { label: 'AI Decision',      description: 'LLM-powered routing',                 icon: Brain,             tone: 'brand'   },
  crmSync:         { label: 'CRM Sync',         description: 'Push contacts to CRM',                icon: ArrowsClockwise,   tone: 'brand'   },
  scheduleMeeting: { label: 'Schedule Meeting', description: 'Meeting / calendar event',            icon: VideoCamera,       tone: 'success' },
  autoReply:       { label: 'Auto Reply',       description: 'Follow up, or task on reply',         icon: ArrowBendUpLeft,   tone: 'brand'   },
}

/** Fallback for a node type that is not in the union (stale saved workflow). */
const FALLBACK: NodeMeta = {
  label: 'Step',
  description: '',
  icon: Lightning,
  tone: 'internal',
}

export const nodeMeta = (type: string | undefined): NodeMeta =>
  NODE_CATALOG[type as NodeType] || FALLBACK

export const nodeLabel = (type: string | undefined): string => nodeMeta(type).label
export const nodeIcon = (type: string | undefined): NodeIcon => nodeMeta(type).icon
export const nodeTone = (type: string | undefined): ToneStyles => TONE_STYLES[nodeMeta(type).tone]

/** MiniMap and any other surface that needs a raw colour value. */
export const nodeHex = (type: string | undefined): string => nodeTone(type).hex

// ---------------------------------------------------------------------------
// Canvas node card chrome — one definition, was copied into all 22 node files
// ---------------------------------------------------------------------------

/**
 * `border-2` is kept deliberately: a canvas node needs presence against the
 * dot-grid background in a way a list row does not (§22 governs list rows).
 * `dark:bg-white/[0.02]` was missing from **all 22** cards — a `bg-card`-only
 * node reads as a hole punched in the dark canvas.
 */
const NODE_CARD_BASE =
  'px-4 py-3 rounded-2xl border-2 bg-card dark:bg-white/[0.02] shadow-card min-w-[180px] ' +
  'hover:bg-accent/30 hover:border-border/80 transition-colors duration-150'

export const nodeCardClass = (type: string | undefined, selected?: boolean): string => {
  const tone = nodeTone(type)
  return `${NODE_CARD_BASE} ${selected ? tone.border : tone.borderIdle}`
}

/** ReactFlow `<Handle>` chrome, tone-coloured. */
export const nodeHandleClass = (type: string | undefined): string =>
  `!w-3 !h-3 !border-2 !border-card ${nodeTone(type).handle}`

/** A handle on a semantic branch outlet rather than the node's own tone. */
export const toneHandleClass = (tone: NodeTone): string =>
  `!w-3 !h-3 !border-2 !border-card ${TONE_STYLES[tone].handle}`

/** 32px soft icon chip inside a node card. */
export const nodeChipClass = (type: string | undefined): string =>
  `w-8 h-8 rounded-xl border flex items-center justify-center ${nodeTone(type).chip}`

/** Icon + eyebrow text colour inside a node card. */
export const nodeTextClass = (type: string | undefined): string => nodeTone(type).text

// ---------------------------------------------------------------------------
// Palette (WorkflowSidebar)
// ---------------------------------------------------------------------------

/**
 * Node types deliberately withheld from the drag palette. They stay fully
 * registered and configurable so any workflow that already contains one keeps
 * working — they are just not offered for new workflows.
 *
 * This replaces three different comment-out styles in `WorkflowSidebar` (block
 * comment, line comment, and pruned icon imports) with one declared list.
 */
export const PALETTE_HIDDEN_TYPES: ReadonlySet<NodeType> = new Set<NodeType>([
  'smsMessage',
  'linkedInAction',
  'aiDecision',
  'goal',
  'scheduleMeeting',
  'httpRequest',
])

export interface PaletteCategory {
  name: string
  types: NodeType[]
}

export const NODE_PALETTE: PaletteCategory[] = [
  { name: 'Triggers',      types: ['trigger'] },
  { name: 'Messages',      types: ['sendEmail', 'generateDeck', 'smsMessage', 'linkedInAction'] },
  { name: 'Data',          types: ['enrichContacts'] },
  { name: 'Flow Control',  types: ['delay', 'condition', 'autoReply', 'abTest', 'rateLimit', 'branchByField', 'aiDecision'] },
  { name: 'Actions',       types: ['action', 'goal', 'exitWorkflow', 'createTask', 'assignOwner'] },
  { name: 'Notifications', types: ['internalAlert'] },
  { name: 'Integrations',  types: ['crmSync', 'scheduleMeeting', 'httpRequest'] },
]

/** The palette as rendered — hidden types removed, empty categories dropped. */
export const visiblePalette = (): PaletteCategory[] =>
  NODE_PALETTE
    .map(c => ({ name: c.name, types: c.types.filter(t => !PALETTE_HIDDEN_TYPES.has(t)) }))
    .filter(c => c.types.length > 0)
